import {Popover, PopoverContent, PopoverTrigger} from "../../ui/popover";
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList} from "../../ui/command";
import {cn} from "../../ui/lib/utils";
import React, {ReactElement, useEffect, useRef, useState} from "react";
import {CheckIcon, XIcon} from "lucide-react";

interface DropDownItem {
    displayName?: string
    value: string
}

interface MultiSelectorDropDownProps {
    allSelector?: string
    selectorSingular: string
    possibleItems: DropDownItem[]
    selectedItems: DropDownItem[]
    setSelectedItems: (value: DropDownItem[]) => void
    isSingleSelect?: boolean
    overrideEmtpySelectionText?: string
    hideSelectedItems?: boolean
    visualSettings?: {
        dropdownWidth?: string
        dropdownAlign?: "start" | "center" | "end";
    }
}

// State is controlled outsdie of the component,
// This component just visualizes the selected items and allows the user to select more and remove them
// When those events happen, we fire the setSelectedItems function to update the state
function MultiSelectorDropDown(props: MultiSelectorDropDownProps) {
    const [open, setOpen] = React.useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
    const [filteredItems, setFilteredItems] = useState<DropDownItem[]>([]);
    const [lastNavigationMethod, setLastNavigationMethod] = useState<'mouse' | 'keyboard'>('mouse');
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    // Update filtered items whenever the dropdown opens or possibleItems change
    useEffect(() => {
        if (open) {
            const items = props.hideSelectedItems
                ? props.possibleItems.filter(item => !isItemSelected(item))
                : props.possibleItems;
            setFilteredItems(items);
            setHighlightedIndex(-1);
            setLastNavigationMethod('mouse');
        }
    }, [open, props.possibleItems, props.hideSelectedItems]);

    // Scroll highlighted item into view
    useEffect(() => {
        if (highlightedIndex >= 0 && listRef.current && lastNavigationMethod === 'keyboard') {
            const highlightedElement = listRef.current.querySelector(`[data-index="${highlightedIndex}"]`);
            if (highlightedElement) {
                highlightedElement.scrollIntoView({ block: 'nearest' });
            }
        }
    }, [highlightedIndex, lastNavigationMethod]);

    // Function to select an item
    const selectItem = (item: DropDownItem) => {
        if (props.isSingleSelect) {
            props.setSelectedItems([item]);
            setOpen(false);
            return;
        }
        
        const selected = isItemSelected(item);
        
        // If already selected, remove it
        if (selected) {
            const newFilter = props.selectedItems.filter(
                (selectedItem) => selectedItem.value !== item.value
            );
            props.setSelectedItems([...newFilter]);
        } else {
            // If not selected, add it
            let newFilter = [...props.selectedItems, item];
            props.setSelectedItems(newFilter);
        }
        setOpen(false);
    };

    let selectedPills: ReactElement[] = [];
    props.selectedItems.forEach((selectedItem) => {
        selectedPills.push(
            <div
                key={selectedItem.value}
                onClick={(e) => {
                    e.stopPropagation();
                }}
                className={"inline-flex items-center bg-backgroundmedium border rounded"}
            >
                <div className={"px-2 py-1"}>
                    <div className={"text-textmedium text-sm"}>{selectedItem.displayName}</div>
                </div>
                <div className={"flex"}>
                    <XIcon 
                        onClick={() => {
                            let newFilter = props.selectedItems.filter((value) => value.value !== selectedItem.value);
                            props.setSelectedItems(newFilter);
                            setOpen(false);
                        }} 
                        className={"h-6 w-6 p-1 hover:cursor-pointer hover:bg-primary rounded-tr rounded-br"}
                    />
                </div>
            </div>
        )
    })

    // Function to check if an item is already selected
    const isItemSelected = (item: DropDownItem) => {
        return props.selectedItems.some(selectedItem => selectedItem.value === item.value);
    };

    // Handle search filter
    const handleSearchChange = (value: string) => {
        if (!value) {
            const items = props.hideSelectedItems
                ? props.possibleItems.filter(item => !isItemSelected(item))
                : props.possibleItems;
            setFilteredItems(items);
            setHighlightedIndex(-1);
            return;
        }

        const lowercaseValue = value.toLowerCase();
        const filtered = props.possibleItems.filter(item => {
            const displayName = item.displayName || item.value;
            return displayName.toLowerCase().includes(lowercaseValue) && 
                !(props.hideSelectedItems && isItemSelected(item));
        });
        
        setFilteredItems(filtered);
        setHighlightedIndex(filtered.length > 0 ? 0 : -1);
        setLastNavigationMethod('keyboard');
    };

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!open) return;

        const items = filteredItems;
        
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setLastNavigationMethod('keyboard');
                setHighlightedIndex(prev => 
                    prev < items.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setLastNavigationMethod('keyboard');
                setHighlightedIndex(prev => 
                    prev > 0 ? prev - 1 : items.length - 1
                );
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < items.length) {
                    selectItem(items[highlightedIndex]);
                }
                break;
            case 'Escape':
                setOpen(false);
                break;
        }
    };

    return (
        <div className={"flex flex-col border bg-backgroundmedium rounded overflow-hidden"}>
            <div className={"flex h-full w-full"}>
                <div className={"text-textmedium bg-backgrounddark px-3 py-2 border-r flex items-center h-full"}>
                   {props.selectorSingular}{!props.selectorSingular && "s"}
                </div>
                <Popover open={open} modal={true}>
                    <PopoverTrigger asChild className={"flex-grow"}>
                        <div 
                            onClick={() => setOpen(true)} 
                            className={"flex justify-start items-center p-2 hover:cursor-pointer min-h-[40px] pr-8"}
                        >
                            <div className={"flex flex-wrap gap-2"}>
                                {selectedPills.length === 0 &&  props.overrideEmtpySelectionText === undefined  && (
                                    <div className={"text-textmedium flex items-center"}>
                                        {props.allSelector ? props.allSelector : !props.isSingleSelect && "All " + props.selectorSingular + "s"}
                                        {props.isSingleSelect && "Select " + props.selectorSingular}
                                    </div>
                                )}
                                {selectedPills.length === 0 && props.overrideEmtpySelectionText !== undefined && (
                                            <div className={"text-textmedium flex items-center"}>
                                                {props.overrideEmtpySelectionText}
                                            </div>
                                        )
                                }
                                {selectedPills}
                            </div>
                        </div>
                    </PopoverTrigger>
                    <PopoverContent 
                        side={"bottom"} 
                        align={props.visualSettings ? props.visualSettings.dropdownAlign : "end"}
                        alignOffset={0}
                        sideOffset={5}
                        avoidCollisions={true}
                        onFocusOutside={() => setOpen(false)}
                        onEscapeKeyDown={() => setOpen(false)}
                        onInteractOutside={() => setOpen(false)}
                        className="p-0 text-textlight bg-backgroundmedium max-w-[40vw] rounded"
                    >
                        <Command 
                            onKeyDown={handleKeyDown}
                            className={"z-50"}
                            shouldFilter={false}
                        >
                            <CommandInput 
                                id={"free_text_input2"}
                                ref={inputRef}
                                onValueChange={handleSearchChange}
                                className={cn("h-12 grow text-textlight ring-0 border-0 shadow-none focus-visible:border-0 focus-visible:ring-0 bg-backgroundmedium")}
                                autoFocus
                            />
                            <CommandList ref={listRef} className={"text-textlight"}>
                                <CommandEmpty>No {props.selectorSingular}s found.</CommandEmpty>
                                <CommandGroup>
                                    {filteredItems.map((item, index) => {
                                        const selected = isItemSelected(item);
                                        const isHighlighted = 
                                            lastNavigationMethod === 'keyboard' 
                                                ? highlightedIndex === index
                                                : false;
                                        
                                        return (
                                            <CommandItem
                                                key={index}
                                                data-index={index}
                                                className={cn(
                                                    "hover:cursor-pointer hover:bg-primarytransparent hover:border hover:border-primary hover:text-textlight",
                                                    isHighlighted && "bg-primarytransparent border border-primary"
                                                )}
                                                onMouseEnter={() => {
                                                    setHighlightedIndex(index);
                                                    setLastNavigationMethod('mouse');
                                                }}
                                                onSelect={() => selectItem(item)}
                                            >
                                                <div className="flex items-center justify-between w-full">
                                                    <span>{item.displayName}</span>
                                                    {selected && <CheckIcon className="h-4 w-4 text-primary" />}
                                                </div>
                                            </CommandItem>
                                        );
                                    })}
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>
            </div>
        </div>
    )
}

function stringArrayToDropDownItemArray(array: string[]): DropDownItem[] {
    return array.map((value) => {
        return {displayName: value, value: value}
    }).filter((value) => value.value !== "")
}

function dropDownItemArrayToStringArray(array: DropDownItem[]): string[] {
    return array.map((value) => {
        return value.value
    })
}

export {
    MultiSelectorDropDown,
    stringArrayToDropDownItemArray,
    dropDownItemArrayToStringArray
}

export type {
    DropDownItem,
    MultiSelectorDropDownProps
}
